/**
 * Second Opinion — client API (X-OBSERVER-001).
 *
 * The learner's side. Reads and writes only rows RLS already scopes to them;
 * the observer's side never touches this module (they have a link, not an
 * account, and go through the `observer-submit` edge function).
 */
import { supabase } from "@/integrations/supabase/client";
import type {
  ApprovedObserverInput,
  ObserverDraft,
  ObserverEditEvent,
  ObserverObservation,
  ObserverRelationship,
} from "@/features/assessment-v2/observer";
import type { Band } from "@/features/assessment-v2/band";

export type ObserverInviteStatus =
  | "sent" | "submitted" | "approved" | "declined" | "expired";

export interface ObserverInvite {
  id: string;
  observer_email: string;
  observer_name: string | null;
  relationship: ObserverRelationship;
  invited_by: "student" | "parent";
  status: ObserverInviteStatus;
  band: Band | null;
  observation: ObserverObservation | null;
  approval: ApprovedObserverInput | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  expires_at: string;
  created_at: string;
  email_sent_at: string | null;
  /** The learner's working copy, pre-approval. Never read by the engine. */
  draft: ObserverDraft | null;
  /** Append-only record of the learner's own review actions. */
  edit_history: ObserverEditEvent[] | null;
  last_resent_at: string | null;
  resend_count: number;
}

const COLUMNS =
  "id, observer_email, observer_name, relationship, invited_by, status, band, observation, approval, " +
  "submitted_at, reviewed_at, expires_at, created_at, email_sent_at, draft, edit_history, " +
  "last_resent_at, resend_count";

export async function listObserverInvites(): Promise<ObserverInvite[]> {
  const { data, error } = await supabase
    .from("observer_invites")
    .select(COLUMNS)
    .order("created_at", { ascending: false });
  if (error) {
    console.warn("[observer] list failed", error.message);
    return [];
  }
  return (data ?? []) as unknown as ObserverInvite[];
}

/** Invites still waiting on the observer, so the UI can chase gently. */
export function isPending(i: ObserverInvite): boolean {
  return i.status === "sent" && new Date(i.expires_at).getTime() > Date.now();
}

/** Submitted and not yet reviewed by the learner — the approval queue. */
export function needsReview(i: ObserverInvite): boolean {
  return i.status === "submitted";
}

/** Ran out of time before they replied. Resendable, and worth saying so. */
export function isExpired(i: ObserverInvite): boolean {
  return (
    i.status === "expired" ||
    (i.status === "sent" && new Date(i.expires_at).getTime() <= Date.now())
  );
}

/** A resend only makes sense while the observer still hasn't answered. */
export function canResend(i: ObserverInvite): boolean {
  return i.invited_by === "student" && (isPending(i) || isExpired(i));
}

export interface InviteStatusView {
  key: "pending" | "expired" | "review" | "approved" | "declined";
  label: string;
  /** Said in the second person, because the learner is the reader. */
  note: string;
}

export function inviteStatusView(i: ObserverInvite): InviteStatusView {
  if (needsReview(i)) {
    return { key: "review", label: "Ready for you", note: "They replied. Nothing counts until you say so." };
  }
  if (i.status === "approved") {
    const kept = (i.approval?.passion_ids.length ?? 0) + (i.approval?.cause_ids.length ?? 0);
    return {
      key: "approved",
      label: "Added by you",
      note: `${kept} thing${kept === 1 ? "" : "s"} you kept and confirmed.`,
    };
  }
  if (i.status === "declined") {
    return { key: "declined", label: "Set aside", note: "None of it counts. Your results are unchanged." };
  }
  if (isExpired(i)) {
    return { key: "expired", label: "Expired", note: "The link ran out before they answered. You can send it again." };
  }
  const days = Math.max(
    0, Math.ceil((new Date(i.expires_at).getTime() - Date.now()) / 86_400_000),
  );
  return {
    key: "pending",
    label: i.email_sent_at ? "Sent, waiting" : "Sent",
    note: `${days} day${days === 1 ? "" : "s"} left for them to reply.`,
  };
}

export interface SendInviteArgs {
  observer_email: string;
  observer_name?: string;
  relationship: ObserverRelationship;
  band?: Band;
  /** Parent offering input on a linked child, rather than the learner asking. */
  mode?: "student_invites" | "parent_offers";
  student_id?: string;
}

export type SendInviteResult =
  | { ok: true; emailSent: boolean; formUrl: string | null }
  | { ok: false; error: string };

const ERROR_COPY: Record<string, string> = {
  invite_limit_reached:
    "You've got three requests open already. Wait for one to come back, or remove one first.",
  invalid_email: "That email address doesn't look right.",
  cannot_invite_self: "That's your own address — pick someone who knows you.",
  not_linked: "This account isn't linked to that learner yet.",
  unauthorized: "Sign in again and give it another go.",
  resend_limit_reached: "You've nudged them three times. Try a different way of asking.",
  already_answered: "They've already replied — check your review list.",
  not_found: "That request isn't there any more.",
};

export async function sendObserverInvite(
  args: SendInviteArgs,
): Promise<SendInviteResult> {
  const { data, error } = await supabase.functions.invoke("observer-invite", {
    body: {
      ...args,
      mode: args.mode ?? "student_invites",
      origin: window.location.origin.startsWith("https://")
        ? window.location.origin
        : undefined,
    },
  });
  const payload = data as { error?: string; email_sent?: boolean; form_url?: string | null } | null;
  const code = payload?.error;
  if (error || code) {
    return { ok: false, error: ERROR_COPY[code ?? ""] ?? "That didn't send. Try again in a moment." };
  }
  return { ok: true, emailSent: Boolean(payload?.email_sent), formUrl: payload?.form_url ?? null };
}

/**
 * Record the learner's decision. `approval` is the ONLY payload the engine
 * ever reads — an unreviewed observation is inert by construction.
 */
export async function saveObserverApproval(
  inviteId: string,
  approval: ApprovedObserverInput,
  outcome: "approved" | "declined",
): Promise<boolean> {
  const { error } = await supabase
    .from("observer_invites")
    .update({
      approval: approval as unknown as never,
      status: outcome,
      reviewed_at: new Date().toISOString(),
    })
    .eq("id", inviteId);
  if (error) {
    console.warn("[observer] approval failed", error.message);
    return false;
  }
  return true;
}

export async function deleteObserverInvite(inviteId: string): Promise<boolean> {
  const { error } = await supabase.from("observer_invites").delete().eq("id", inviteId);
  if (error) {
    console.warn("[observer] delete failed", error.message);
    return false;
  }
  return true;
}

/**
 * The single approved input the engine should use: the most recently approved
 * one. We deliberately do not merge several observers — two approvals would
 * compound into a louder-than-first-person signal, which is the one thing
 * this channel must never become.
 */
export function latestApproval(
  invites: ObserverInvite[],
): { invite: ObserverInvite; approval: ApprovedObserverInput } | null {
  const approved = invites
    .filter((i) => i.status === "approved" && i.approval)
    .sort((a, b) => (b.reviewed_at ?? "").localeCompare(a.reviewed_at ?? ""));
  const top = approved[0];
  return top ? { invite: top, approval: top.approval as ApprovedObserverInput } : null;
}

/**
 * Save the learner's working copy and append to their change history.
 *
 * History is append-only from the client's point of view: we write the whole
 * array back, but only ever by adding to what we read. A learner asking "what
 * did I change?" deserves a straight answer, and a rewritten history is not
 * one.
 */
export async function saveObserverDraft(
  inviteId: string,
  draft: ObserverDraft,
  history: ObserverEditEvent[],
): Promise<boolean> {
  const { error } = await supabase
    .from("observer_invites")
    .update({
      draft: draft as unknown as never,
      edit_history: history as unknown as never,
    })
    .eq("id", inviteId);
  if (error) {
    console.warn("[observer] draft save failed", error.message);
    return false;
  }
  return true;
}

/** Send the same link again, with a fresh 30 days on it. */
export async function resendObserverInvite(
  inviteId: string,
): Promise<{ ok: true; emailSent: boolean } | { ok: false; error: string }> {
  const { data, error } = await supabase.functions.invoke("observer-invite", {
    body: {
      mode: "resend",
      invite_id: inviteId,
      origin: window.location.origin.startsWith("https://")
        ? window.location.origin
        : undefined,
    },
  });
  const payload = data as { error?: string; email_sent?: boolean } | null;
  if (error || payload?.error) {
    return {
      ok: false,
      error: ERROR_COPY[payload?.error ?? ""] ?? "That didn't send. Try again in a moment.",
    };
  }
  return { ok: true, emailSent: Boolean(payload?.email_sent) };
}
